import React, { useEffect, useMemo, useState } from "react";
import { amountDataToDisplay } from "../utils/utils";
import { openVenmoPay } from "../services/Venmo";
import { useTranslation } from "react-i18next";
import { ICONS } from "../constants";
import ZelleQrBlock from "./ZelleQrBlock";

export default function SettlementItemModal(props) {
  const { t } = useTranslation();
  const {
    modalId,
    groupName,
    fromContributor,
    toContributor,
    amountCents,
    isPaid = false,
    onClose,
    onMarkPaid,
  } = props;

  const canPayWithVenmo = useMemo(() => {
    const u = toContributor?.venmoUsername;
    return !!(u && String(u).trim());
  }, [toContributor?.venmoUsername]);

  const canPayWithZelle = useMemo(() => {
    const tkn = toContributor?.zelleUsername;
    const nm = toContributor?.zelleRecipientName;
    return !!(tkn && String(tkn).trim() && nm && String(nm).trim());
  }, [toContributor?.zelleUsername, toContributor?.zelleRecipientName]);

  const isProbablyMobile = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /Android|iPhone|iPad|iPod/i.test(ua);
  }, []);

  const paidLabel = t("general.paid", { defaultValue: "Paid" });
  const markPaidLabel = t("general.mark_paid", { defaultValue: "Mark paid" });
  const payWithVenmoLabel = t("general.pay_with_venmo", { defaultValue: "Pay with Venmo" });
  const payWithZelleLabel = t("general.pay_with_zelle", { defaultValue: "Pay with Zelle" });

  const [showZellePay, setShowZellePay] = useState(false);

  useEffect(() => {
    setShowZellePay(false);
  }, [
    toContributor?.id,
    toContributor?.zelleUsername,
    toContributor?.zelleRecipientName,
    amountCents,
    isPaid,
  ]);

  // Modal body grows when Zelle QR mounts; keep backdrop/layout in sync.
  useEffect(() => {
    if (!showZellePay || typeof document === "undefined") return;
    const el = document.getElementById(modalId);
    if (!el || !window?.bootstrap?.Modal) return;
    try {
      const inst =
        window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
      inst.handleUpdate?.();
    } catch {
      // ignore
    }
  }, [showZellePay, modalId]);

  const handleClose = () => {
    setShowZellePay(false);
    if (typeof onClose === "function") onClose();
  };

  const title = t("general.settlement_item", { defaultValue: "Settlement item" });

  return (
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-modal as-theme">
          <div className="modal-header">
            <div className="as-modal-header-row" style={{ width: "100%" }}>
              <div className="as-modal-title-row" style={{ minWidth: 0 }}>
                <span className="as-modal-icon-badge" aria-hidden="true">
                  💸
                </span>
                <h5 className="modal-title text-truncate" style={{ maxWidth: "100%" }}>
                  {title}
                </h5>
              </div>
              <div className="as-modal-actions">
                <button type="button" className="btn-close" onClick={handleClose} aria-label={t("general.close", { defaultValue: "Close" })} />
              </div>
            </div>
          </div>

          <div className="modal-body">
            <div className="d-flex align-items-start justify-content-between" style={{ gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate">
                  <span aria-hidden="true">
                    {ICONS?.[fromContributor?.icon] || "🐾"}
                  </span>{" "}
                  {fromContributor?.name || t("general.someone", { defaultValue: "Someone" })}{" "}
                  {t("general.owes", { defaultValue: "owes" })}{" "}
                  <span aria-hidden="true">
                    {ICONS?.[toContributor?.icon] || "🐾"}
                  </span>{" "}
                  {toContributor?.name || t("general.someone", { defaultValue: "Someone" })}
                </div>
                <div className="text-muted">
                  {t("general.amount", { defaultValue: "Amount" })}: ${amountDataToDisplay(amountCents || 0)}
                </div>
                <div className="mt-1">
                  <span className={`badge ${isPaid ? "bg-success" : "bg-secondary"}`}>
                    {isPaid ? paidLabel : t("general.unpaid", { defaultValue: "Unpaid" })}
                  </span>
                </div>
              </div>
            </div>

            {canPayWithZelle && showZellePay ? (
              <ZelleQrBlock
                token={toContributor?.zelleUsername}
                recipientName={toContributor?.zelleRecipientName}
              />
            ) : null}
          </div>

          <div className="modal-footer d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {canPayWithVenmo && isProbablyMobile ? (
                <button
                  type="button"
                  className="btn as-btn-venmo"
                  onClick={() =>
                    openVenmoPay({
                      username: toContributor?.venmoUsername,
                      amountCents: amountCents || 0,
                      note: groupName,
                      sourceModalId: modalId,
                    })
                  }
                >
                  {payWithVenmoLabel}
                </button>
              ) : null}
              {canPayWithZelle && !isPaid && !showZellePay ? (
                <button
                  type="button"
                  className="btn as-btn-zelle"
                  onClick={() => setShowZellePay(true)}
                >
                  {payWithZelleLabel}
                </button>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                {t("general.close", { defaultValue: "Close" })}
              </button>
              {typeof onMarkPaid === "function" ? (
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={isPaid}
                  onClick={() => {
                    if (isPaid) return;
                    onMarkPaid();
                  }}
                >
                  {isPaid ? paidLabel : markPaidLabel}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

